import React, { useEffect, useState, useMemo } from 'react';
import { navigate } from 'hooks/useHookRouterCompat';
import styled from 'styled-components';
import Vendor from 'data/Vendor';
import LoadingIndicator from 'components/UI/LoadingIndicator';
import { renderToast, toast } from 'components/UI/ToastNotification';
import { useAPI } from 'lib/API';
import { loadBrands } from 'requests/brand';
import BrandHeader from './BrandHeader';
import Brand from 'data/Brand';
import BrandsListing from './BrandsListing';
import User from 'data/User';
import { Size } from 'lib/styles';
import { BRAND_CONSTANTS } from 'constants/brands';
import FeatureFlag from 'data/FeatureFlag';
import { BusinessUnit } from 'data/BusinessUnit';
import { useUserRole } from 'hooks/useUserRole';
import { useBusinessUnitBrandFiltering } from 'hooks/useBusinessUnitBrandFiltering';

const Page = styled.div`
  margin: ${Size.semi} 0px;
  width: 100%;
  padding: 0 25px;
`;

const EmptyMessage = styled.span`
  font-size: 24px;
  padding: 0px;
`;

interface Props {
  selectedVendor?: Vendor;
  user: User;
  featureFlags: FeatureFlag;
  selectedBusinessUnit: BusinessUnit;
}

const BrandsV2 = ({ selectedVendor, user, featureFlags, selectedBusinessUnit }: Props) => {
  const [allBrands, setAllBrands] = useState<Brand[]>([]);
  const [filteredBrands, setFilteredBrands] = useState<Brand[]>([]);
  const userRole = useUserRole({ user, selectedVendor, selectedBusinessUnit }).userRole;

  // Use the brand filtering hook
  const { brandIdsForFiltering } = useBusinessUnitBrandFiltering({
    selectedBusinessUnit,
    selectedVendorId: selectedVendor?.id
  });

  const brandsReq = useAPI({
    deferFn: loadBrands,
    onResolve: (data: Brand[]) => {
      setAllBrands(data);
    },
    onReject: (err: any) => {
      console.log(err);
    }
  });

  // Use useMemo to optimize filtering
  const memoizedFilteredBrands = useMemo(() => {
    if (!selectedBusinessUnit || !brandIdsForFiltering) {
      return allBrands;
    }

    // Build a complete set of brand IDs including parent/child relationships
    const expandedBrandIds = new Set<number>();

    brandIdsForFiltering.forEach(brandId => {
      const brand = allBrands.find(b => b.id === brandId);

      if (!brand) return;

      // Add the brand itself
      expandedBrandIds.add(brandId);

      // If this is a parent brand, add all its children
      if (brand.isParentBrand) {
        allBrands.forEach(b => {
          if (b.parentId === brandId) {
            expandedBrandIds.add(b.id);
          }
        });
      }

      // If this is a child brand, add its parent and all siblings
      if (brand.parentId) {
        // Add the parent
        expandedBrandIds.add(brand.parentId);

        // Add all siblings (other children of the same parent)
        allBrands.forEach(b => {
          if (b.parentId === brand.parentId) {
            expandedBrandIds.add(b.id);
          }
        });
      }
    });

    return allBrands.filter(brand => expandedBrandIds.has(brand.id));
  }, [allBrands, selectedBusinessUnit, brandIdsForFiltering]);

  // Update filteredBrands when memoized value changes
  useEffect(() => {
    setFilteredBrands(memoizedFilteredBrands);
  }, [memoizedFilteredBrands]);

  useEffect(() => {
    brandsReq.run(selectedVendor?.id);
  }, [selectedVendor, selectedBusinessUnit]);


  const createButtonClick = () => {
    if (!selectedVendor) {
      renderToast(toast.TYPE.INFO, BRAND_CONSTANTS.SELECT_VENDOR_BEFORE_CREATING_BRAND);
      return;
    } else {
      navigate(`/brands/new-brand`);
    }
  };

  const renderContent = () => {
    if (brandsReq.isLoading) {
      return <LoadingIndicator center />;
    }

    if (!selectedVendor) {
      return <EmptyMessage>{BRAND_CONSTANTS.SELECT_VENDOR_TO_SHOW_BRANDS}</EmptyMessage>;
    }

    if (filteredBrands.length === 0) {
      return <EmptyMessage>{BRAND_CONSTANTS.NO_BRANDS_FOR_SELECTED_VENDOR}</EmptyMessage>;
    }

    return <BrandsListing brands={filteredBrands} featureFlags={featureFlags} />;
  };

  return (
    <Page>
      <BrandHeader onCreate={createButtonClick} userRole={userRole} />
      {
        renderContent()
      }
    </Page>
  );
};


export default React.memo(BrandsV2);
